import { useCallback, useState } from 'react';
import dayjs from 'dayjs';

const STORAGE_PREFIX = 'dashboard_widget_filter_';

interface WidgetFilters {
	dateFrom: Date;
	dateTo: Date;
}

const getDefaults = (): WidgetFilters => ({
	dateFrom: dayjs().startOf('year').toDate(),
	dateTo: dayjs().endOf('month').toDate(),
});

const loadFromStorage = (widgetId: string): WidgetFilters | null => {
	try {
		const raw = localStorage.getItem(STORAGE_PREFIX + widgetId);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		return {
			dateFrom: new Date(parsed.dateFrom),
			dateTo: new Date(parsed.dateTo),
		};
	} catch {
		return null;
	}
};

const saveToStorage = (widgetId: string, filters: WidgetFilters) => {
	try {
		localStorage.setItem(STORAGE_PREFIX + widgetId, JSON.stringify(filters));
	} catch {
		// Silent fail
	}
};

export const clearAllWidgetFilters = () => {
	try {
		const keysToRemove: string[] = [];
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (key && key.startsWith(STORAGE_PREFIX)) {
				keysToRemove.push(key);
			}
		}
		keysToRemove.forEach(k => localStorage.removeItem(k));
	} catch {
		// Silent fail
	}
};

const useWidgetFilters = (widgetId: string) => {
	const [filters, setFilters] = useState<WidgetFilters>(() => loadFromStorage(widgetId) ?? getDefaults());

	const setDateRange = useCallback((dateFrom: Date, dateTo: Date) => {
		const next = { dateFrom, dateTo };
		setFilters(next);
		saveToStorage(widgetId, next);
	}, [widgetId]);

	return {
		dateFrom: filters.dateFrom,
		dateTo: filters.dateTo,
		setDateRange,
	};
};

export default useWidgetFilters;
